import { memo, useCallback, useEffect } from "react";
import { useTranslation } from 'react-i18next';
import type { SavedView, Task } from "../../types";
import {
  useShowImportModal, useImportText, useImportError, useUIActions,
  useRoadmapActions, useSkillsActions, useEmployeesActions, useTeamsActions,
  useColumnsActions, useGlobalRequirementsActions, recalculateSchedule, importTaskIdCounter,
} from "../../stores";
import { businessDayIndexToDate, BOARD_START_DATE } from "../../lib/dateUtils";
import { useModalDrag } from "../../hooks/useModalDrag";
import { X } from "lucide-react";

function migrateStringToBlocks(val: string | unknown[] | undefined): unknown[] | undefined {
  if (!val) return undefined;
  if (Array.isArray(val)) return val;
  if (typeof val === 'string') return [{ type: 'paragraph', content: [{ type: 'text', text: val }] }];
  return undefined;
}

export const ImportModal = memo(function ImportModal() {
  const { t } = useTranslation();
  const showModal = useShowImportModal();
  const importText = useImportText();
  const importError = useImportError();
  const { closeImportModal, setImportText, setImportError } = useUIActions();
  const { importTasks, addTask } = useRoadmapActions();
  const { importSkills } = useSkillsActions();
  const { importEmployees } = useEmployeesActions();
  const { importTeams } = useTeamsActions();
  const { importColumns } = useColumnsActions();
  const { importRequirements } = useGlobalRequirementsActions();
  const { modalRef, position, isDragging, handleMouseDown, handleDoubleClick } = useModalDrag();

  const handleImport = useCallback(() => {
    try {
      const parsed = JSON.parse(importText) as SavedView;
      if (parsed.version !== 1) { setImportError(t('importModal.invalidVersion')); return; }
      if (!Array.isArray(parsed.tasks) && !Array.isArray(parsed.allTasks)) { setImportError(t('importModal.invalidFormat')); return; }
      if (Array.isArray(parsed.teams) && parsed.teams.length > 0) importTeams(parsed.teams);
      if (Array.isArray(parsed.allTasks) && parsed.allTasks.length > 0) {
        for (const taskData of parsed.allTasks) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const raw = taskData as any;
          const task: Task = {
            id: taskData.id, title: taskData.title, column: taskData.column,
            startDate: taskData.startDate || (typeof raw.startDay === 'number' ? businessDayIndexToDate(raw.startDay) : BOARD_START_DATE),
            estimateMode: taskData.estimateMode || 'default',
            ...(taskData.defaultEstimate && { defaultEstimate: taskData.defaultEstimate }),
            estimates: taskData.estimates, assigneeIds: taskData.assigneeIds,
            ...(taskData.description && { description: migrateStringToBlocks(taskData.description as string | unknown[]) }),
            ...(taskData.consequence && { consequence: migrateStringToBlocks(taskData.consequence as string | unknown[]) }),
            ...(taskData.requiredSkills && { requiredSkills: taskData.requiredSkills }),
            categoryIds: taskData.categoryIds ?? [],
            ...(taskData.requirements_hard && { requirements_hard: taskData.requirements_hard }),
            ...(taskData.requirements_soft && { requirements_soft: taskData.requirements_soft }),
          };
          addTask(task);
        }
      } else {
        importTasks(parsed.tasks);
        if (Array.isArray(parsed.newTasks)) {
          for (const newTask of parsed.newTasks) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const rawNew = newTask as any;
            const task: Task = {
              id: newTask.id, title: newTask.title, column: newTask.column,
              startDate: newTask.startDate || (typeof rawNew.startDay === 'number' ? businessDayIndexToDate(rawNew.startDay) : BOARD_START_DATE),
              estimateMode: newTask.estimateMode || 'default',
              ...(newTask.defaultEstimate && { defaultEstimate: newTask.defaultEstimate }),
              estimates: newTask.estimates, assigneeIds: newTask.assigneeIds,
              ...(newTask.description && { description: migrateStringToBlocks(newTask.description as string | unknown[]) }),
              ...(newTask.consequence && { consequence: migrateStringToBlocks(newTask.consequence as string | unknown[]) }),
              ...(newTask.requiredSkills && { requiredSkills: newTask.requiredSkills }),
              categoryIds: newTask.categoryIds ?? [],
              ...(newTask.requirements_hard && { requirements_hard: newTask.requirements_hard }),
              ...(newTask.requirements_soft && { requirements_soft: newTask.requirements_soft }),
            };
            addTask(task);
          }
        }
      }
      if (Array.isArray(parsed.skills) && parsed.skills.length > 0) importSkills(parsed.skills);
      if (Array.isArray(parsed.employees) && parsed.employees.length > 0) importEmployees(parsed.employees);
      if (Array.isArray(parsed.columns) && parsed.columns.length > 0) importColumns(parsed.columns);
      if (Array.isArray(parsed.globalRequirements) && parsed.globalRequirements.length > 0) importRequirements(parsed.globalRequirements);
      if (typeof parsed.taskIdCounter === 'number') importTaskIdCounter(parsed.taskIdCounter);
      closeImportModal();
      setTimeout(() => recalculateSchedule(), 0);
    } catch {
      setImportError(t('importModal.invalidJson'));
    }
  }, [importText, importTasks, addTask, importSkills, importEmployees, importTeams, importColumns, importRequirements, closeImportModal, setImportError]);

  useEffect(() => {
    if (!showModal) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeImportModal();
      if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) handleImport();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showModal, closeImportModal, handleImport]);

  if (!showModal) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-50" onClick={closeImportModal} />
      <div className="fixed inset-0 z-[51] flex items-center justify-center p-4 pointer-events-none">
        <div
          ref={modalRef}
          className={`bg-card border border-border rounded-lg w-full max-w-md shadow-lg ${position ? '' : 'pointer-events-auto'}`}
          style={position ? { position: 'fixed', left: position.x, top: position.y, pointerEvents: 'auto' } : undefined}
          onClick={(e) => e.stopPropagation()}
        >
          <div
            className="flex items-center justify-between px-3 py-1.5 border-b border-border"
            onMouseDown={handleMouseDown}
            onDoubleClick={handleDoubleClick}
            style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
          >
            <h2 className="text-[11px] font-medium">{t('importModal.title')}</h2>
            <button onClick={closeImportModal} onMouseDown={e => e.stopPropagation()} className="text-gray-400 hover:text-gray-600">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
          <div className="px-3 py-2">
            <textarea
              className="w-full h-40 p-2 border border-border rounded bg-background text-foreground text-[11px] font-mono resize-none focus:outline-none focus:ring-1 focus:ring-primary"
              placeholder={t('importModal.placeholder')} value={importText} onChange={(e) => setImportText(e.target.value)}
              autoFocus
            />
            {importError && <div className="mt-1 text-[10px] text-red-500">{importError}</div>}
          </div>
          <div className="px-3 py-1.5 border-t border-border flex items-center justify-between">
            <span className="text-[9px] text-gray-400">{t('importModal.escHint')}</span>
            <div className="flex gap-1.5">
              <button onClick={closeImportModal} className="px-2 py-1 rounded bg-muted hover:bg-muted/80 text-[10px]">{t('common.cancel')}</button>
              <button onClick={handleImport} className="px-2 py-1 rounded bg-primary text-primary-foreground hover:bg-primary/90 text-[10px]">{t('importModal.importButton')}</button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
});
